// pages/Profile.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, ConfigProvider, Card, Spin } from "antd";
import {
  UserOutlined,
  BankOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSEO } from "../../hooks/useSEO";
import UserAuth from "../../components/middleware/UserAuth";
import { useTheme } from "../../theme/ThemeProvider";
import { useUrlTab } from "../../hooks/useUrlTab";
import { LOGIN_ROUTE, PROFILE_ROUTE } from "../../routes/constant";
import { getUserProfile } from "../../store/slices/profileSlice";
import { clearProfileError } from "../../store/slices/profileSlice";
import ProfileView from "../../components/user/profile/ProfileView";
import ProfileEditForm from "../../components/user/profile/ProfileEditForm";
import BankView from "../../components/user/bank-account/BankView";
import BankEditForm from "../../components/user/bank-account/BankEditForm";
import OrdersTab from "../../components/user/lead/OrdersTab";
import toast from "react-hot-toast";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentTab, setTab } = useUrlTab("profile");

  const { user, loading, error } = useSelector((state) => state.profile);
  const { isLoggedIn } = useSelector((state) => state.auth);

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch user profile on mount only if not logged in or no user
  useEffect(() => {
    if (!user && !loading && isLoggedIn) {
      dispatch(getUserProfile());
    }
  }, [dispatch, user, loading, isLoggedIn]);

  // Handle profile errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearProfileError());
    }
  }, [error, dispatch]);

  // Handle auth errors - redirect to login
  useEffect(() => {
    if (!isLoggedIn && !loading) {
      navigate(LOGIN_ROUTE);
    }
  }, [isLoggedIn, loading, navigate]);

  // Use SEO hook with current tab
  useSEO(PROFILE_ROUTE, { tab: currentTab });

  const handleTabChange = (key) => {
    setTab(key);
    setIsEditingProfile(false);
    setIsEditingBank(false);
  };

  const tabItems = [
    {
      key: "profile",
      label: (
        <div className="flex items-center gap-2">
          <UserOutlined className="text-sm" />
          <span className="text-sm">Profile</span>
        </div>
      ),
      children: isEditingProfile ? (
        <ProfileEditForm
          onCancel={() => setIsEditingProfile(false)}
          isDarkMode={isDarkMode}
        />
      ) : (
        <ProfileView
          onEdit={() => setIsEditingProfile(true)}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      key: "bank",
      label: (
        <div className="flex items-center gap-2">
          <BankOutlined className="text-sm" />
          <span className="text-sm">Bank Details</span>
        </div>
      ),
      children: isEditingBank ? (
        <BankEditForm
          onCancel={() => setIsEditingBank(false)}
          isDarkMode={isDarkMode}
        />
      ) : (
        <BankView
          onEdit={() => setIsEditingBank(true)}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      key: "orders",
      label: (
        <div className="flex items-center gap-2">
          <ShoppingOutlined className="text-sm" />
          <span className="text-sm">Orders</span>
        </div>
      ),
      children: <OrdersTab isDarkMode={isDarkMode} />,
    },
  ];

  // Show loading state
  if (loading && !user) {
    return (
      <UserAuth>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spin size="large" />
        </div>
      </UserAuth>
    );
  }

  // If not logged in and not loading, show nothing (will redirect)
  if (!isLoggedIn && !loading) {
    return null;
  }

  return (
    <UserAuth>
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8 sm:py-8">
        <ConfigProvider
          theme={{
            components: {
              Tabs: {
                itemColor: isDarkMode ? "#9ca3af" : "#6b7280",
                itemSelectedColor: "#22c55e",
                inkBarColor: "#22c55e",
                itemHoverColor: "#22c55e",
              },
            },
          }}
        >
          {isMobile ? (
            <Card className="border-0 shadow-sm rounded-xl">
              <Tabs
                activeKey={currentTab}
                onChange={handleTabChange}
                centered
                size="middle"
                items={tabItems}
              />
            </Card>
          ) : (
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="flex-shrink-0 lg:w-64">
                <Card className="sticky border-0 shadow-sm rounded-xl top-24">
                  <Tabs
                    activeKey={currentTab}
                    onChange={handleTabChange}
                    tabPosition="left"
                    size="middle"
                    items={tabItems.map((item) => ({
                      key: item.key,
                      label: item.label,
                      children: null,
                    }))}
                  />
                </Card>
              </div>
              <div className="flex-1">
                {tabItems.find((item) => item.key === currentTab)?.children}
              </div>
            </div>
          )}
        </ConfigProvider>
      </div>
    </UserAuth>
  );
};

export default Profile;
